// src/lib/exportPdf.ts
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* =========================================================
   SysMindX Phase-11 Export Engine
   — Nearest-Clean-Break Slicing + Per-Page Section Titles

   Phase-10 problem:
     "Push entire card to next page" created large whitespace
     gaps when a card started early in the page (e.g. 40% in)
     and still straddled the boundary — the whole bottom half
     of the page was left empty.

   Phase-11 strategy — two-level candidates, scan backward:
     1. Collect card-level AND sub-section-level break points
        from the live DOM before html2canvas runs.
     2. At each page boundary, scan backward from the natural
        cut to find the NEAREST clean break within MAX_SKIP
        (30% of page height). Cut there.
     3. If no clean break is nearby (large block with no
        internal sub-sections near the boundary): accept the
        natural cut — page stays full, only that one cut is
        through content.
     4. On every overflow page (2nd, 3rd… page of a chapter),
        draw a compact section-title header using jsPDF so the
        reader always knows which section they are in.

   Result:
     - Pages are always ≥ 70% full (max 30% bottom whitespace).
     - Cuts land between cards, or between sub-sections within
       a card, rather than mid-sentence.
     - Every continuation page shows the section title.
   ========================================================= */

export const CHAPTER_ORDER = [
  "COVER",
  "SUMMARY",       // static data-driven snapshot
  "AI_SUMMARY",    // AI-generated narrative (comparison)
  "HOW_TO_USE",
  "KARMIC_OVERVIEW",
  "REMEDIES",
  "AWARENESS",
  "BIRTH_CHART",
  "TRAITS",
  "HOUSES",
  "STRENGTH",
  "CONJUNCTIONS",
  "YOGAS",
  "DASHA",
  "GLOSSARY",
];

/* ---------------------------------------------------------
   Filename builder
---------------------------------------------------------- */

function safeFilePart(x?: string) {
  const s = String(x || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");
  return s || "";
}

export function buildExportFilename(result: any) {
  const name = safeFilePart(result?.received?.name);
  const dob = safeFilePart(result?.received?.dob);
  if (name && dob) return `SysMindX_Astro_Report_${name}_${dob}.pdf`;
  if (name) return `SysMindX_Astro_Report_${name}.pdf`;
  return `SysMindX_Astro_Report.pdf`;
}

/* ---------------------------------------------------------
   Page / margin constants
---------------------------------------------------------- */

const PAGE_W_MM  = 210;
const PAGE_H_MM  = 297;
const MARGIN_L_MM = 10;
const MARGIN_R_MM = 10;
const MARGIN_T_MM = 12;
const MARGIN_B_MM = 18; // reserved for footer

const CONTENT_W_MM = PAGE_W_MM - MARGIN_L_MM - MARGIN_R_MM; // 190
const CONTENT_H_MM = PAGE_H_MM - MARGIN_T_MM - MARGIN_B_MM; // 267

/** Must match the `scale` option passed to html2canvas. */
const HTML2CANVAS_SCALE = 1.8;

/** Height of the continuation header strip on overflow pages (mm). */
const CONT_HEADER_H_MM = 13;

/**
 * Maximum distance (as a fraction of full-page slice height) that the
 * slicer will scan backward from the natural boundary to find a clean
 * break point. Pages will always be at least (1 − MAX_SKIP_RATIO) full.
 *
 * 0.30 → pages are always ≥ 70% full, cuts within last 30% of page.
 */
const MAX_SKIP_RATIO = 0.30;

/**
 * Minimum page fill before any clean-break candidate is considered.
 * Prevents degenerate near-empty pages.
 */
const MIN_FILL_RATIO = 0.20;

/* Chapter display titles for continuation headers and TOC */
const CHAPTER_TITLES: Record<string, string> = {
  COVER:          "",
  SUMMARY:        "Report Snapshot",
  AI_SUMMARY:     "AI Generated Summary",
  HOW_TO_USE:     "How to Use This Report",
  KARMIC_OVERVIEW:"Karmic Overview",
  REMEDIES:       "Karmic Remedies",
  AWARENESS:      "Awareness",
  BIRTH_CHART:    "Birth Chart",
  TRAITS:         "Personality Traits",
  HOUSES:         "Planet in Houses",
  STRENGTH:       "Strong & Weak Planets",
  CONJUNCTIONS:   "Planet Conjunctions",
  YOGAS:          "Yoga Analysis",
  DASHA:          "Vimshottari Dasha",
  GLOSSARY:       "Glossary & Resources",
};

/* ---------------------------------------------------------
   Break-candidate detection

   Two tiers of candidates are collected from the live DOM
   before html2canvas captures the chapter:

   Tier 1 — Card-level (top-level containers):
     .sx-planet-card, .sx-domain-card, .sx-yoga-card, etc.
     Cutting before these is ideal: zero content is split.
     Nested inner cards (e.g. yoga-card inside planet-card)
     are excluded — the outer container is the break unit.

   Tier 2 — Sub-section-level (inside cards):
     .sx-sub-label elements that are NOT the first sub-label
     within their parent card. Cutting before the 2nd, 3rd…
     sub-label keeps the card header + first sub-section
     visible on the current page and moves remaining sub-
     sections cleanly to the next page.

   All positions are multiplied by `scale` to map 1:1 with
   canvas pixels (html2canvas output resolution).
---------------------------------------------------------- */

interface BreakCandidate {
  /** Canvas pixels from the top of the chapter element. */
  top: number;
  bottom: number;
}

/* Selectors for Tier-1 (top-level containers). */
const CARD_SELECTORS = [
  ".sx-planet-card",
  ".sx-domain-card",
  ".sx-awareness-card",
  ".sx-yoga-card",
  ".sx-dasha-block",
  ".sx-dasha-current",
  ".sx-card",
  ".sx-summary-row",
  ".sx-strength-row",
];

/* Selectors whose presence as a DOM ancestor marks an element as nested. */
const CONTAINER_SELECTORS = [
  ".sx-planet-card",
  ".sx-domain-card",
  ".sx-awareness-card",
];

function isNestedInCard(el: Element, root: Element): boolean {
  let ancestor = el.parentElement;
  while (ancestor && ancestor !== root) {
    for (const sel of CONTAINER_SELECTORS) {
      if (ancestor.matches(sel)) return true;
    }
    ancestor = ancestor.parentElement;
  }
  return false;
}

/**
 * Returns true if `subLabel` is the FIRST .sx-sub-label inside its
 * nearest card ancestor. We skip these to prevent orphaning the card
 * header (cutting before the first sub-label would leave only the
 * planet/card title on the current page with no content).
 */
function isFirstSubLabelInCard(subLabel: Element, root: Element): boolean {
  const cardSelectors = [
    ".sx-planet-card",
    ".sx-domain-card",
    ".sx-awareness-card",
    ".sx-yoga-card",
    ".sx-card",
  ].join(",");

  let cardAncestor: Element | null = null;
  let ancestor = subLabel.parentElement;
  while (ancestor && ancestor !== root) {
    if (ancestor.matches(cardSelectors)) {
      cardAncestor = ancestor;
      break;
    }
    ancestor = ancestor.parentElement;
  }

  if (!cardAncestor) {
    // Sub-label is not inside any recognised card — it's standalone.
    // Treat as a valid break point (return false = not the first).
    return false;
  }

  const allSubLabels = Array.from(
    cardAncestor.querySelectorAll(".sx-sub-label")
  );
  return allSubLabels.indexOf(subLabel) === 0;
}

/**
 * Collects all break candidates (Tier-1 cards + Tier-2 sub-sections)
 * from the chapter container, sorted by ascending top position.
 */
function getBreakCandidates(
  el: HTMLElement,
  scale: number
): BreakCandidate[] {
  const elRect = el.getBoundingClientRect();
  const candidates: BreakCandidate[] = [];
  const seen = new Set<Element>();

  // ── Tier 1: Top-level card containers ───────────────────────────
  for (const selector of CARD_SELECTORS) {
    const nodes = el.querySelectorAll(selector);
    for (const node of nodes) {
      if (seen.has(node)) continue;
      if (isNestedInCard(node, el)) continue;
      seen.add(node);

      const rect = (node as HTMLElement).getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;

      const top    = (rect.top    - elRect.top) * scale;
      const bottom = (rect.bottom - elRect.top) * scale;
      if (top >= 0 && bottom > top) candidates.push({ top, bottom });
    }
  }

  // ── Tier 2: Sub-section labels inside cards ──────────────────────
  // Cutting before the 2nd+ .sx-sub-label of a card places the card
  // header + first sub-section on the current page and moves the rest
  // to the next — much better than a mid-paragraph cut.
  const subLabels = el.querySelectorAll(".sx-sub-label");
  for (const sl of subLabels) {
    // Skip the first sub-label of each card to prevent orphaned headers
    if (isFirstSubLabelInCard(sl, el)) continue;

    const rect = (sl as HTMLElement).getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) continue;

    const top    = (rect.top    - elRect.top) * scale;
    const bottom = (rect.bottom - elRect.top) * scale;
    if (top >= 0 && bottom > top) candidates.push({ top, bottom });
  }

  candidates.sort((a, b) => a.top - b.top);
  return candidates;
}

/* ---------------------------------------------------------
   Smart cut-point finder — scan backward from boundary

   Searches for the HIGHEST-top candidate that:
   (a) falls before the natural page boundary (targetY)
   (b) is within MAX_SKIP_RATIO of the boundary
       → ensures pages are always ≥ (1 − MAX_SKIP_RATIO) full
   (c) is past the minimum fill point
       → prevents degenerate near-empty pages

   If no candidate satisfies these constraints, returns
   targetY (natural cut — page is 100% full, content cut
   through, but this is the unavoidable fallback).
---------------------------------------------------------- */

function findSmartCutY(
  targetY: number,
  startY: number,
  candidates: BreakCandidate[],
  sliceHeightPx: number
): number {
  const maxSkipPx  = sliceHeightPx * MAX_SKIP_RATIO;
  const minFillPx  = sliceHeightPx * MIN_FILL_RATIO;

  let bestCutY: number | null = null;

  for (const c of candidates) {
    const cutY = c.top - 2; // 2 canvas-px above the element's top border
    if (
      cutY > startY + minFillPx &&  // enough content on current page
      cutY < targetY &&             // before the natural boundary
      cutY >= targetY - maxSkipPx   // within the max-skip window
    ) {
      // Among all valid candidates, take the one closest to targetY
      // (highest cutY = least whitespace at the bottom of the page).
      if (bestCutY === null || cutY > bestCutY) {
        bestCutY = cutY;
      }
    }
  }

  return bestCutY ?? targetY;
}

/* ---------------------------------------------------------
   Continuation header

   Drawn with jsPDF vector/text APIs — no canvas needed.
   Appears at the top of every overflow page within a chapter.
   Matches the report's amber/navy design language.

   Layout (all within top margin area + CONT_HEADER_H_MM):

     ┌──────────────────────────────────────────────────┐
     │ ▌  Strong & Weak Planets          (continued)    │
     └──────────────────────────────────────────────────┘ ← amber line
         ↑ 13mm total, sits at y = MARGIN_T_MM

   Canvas content on continuation pages starts at:
     MARGIN_T_MM + CONT_HEADER_H_MM = 25mm from page top
---------------------------------------------------------- */

function drawContinuationHeader(pdf: jsPDF, title: string): void {
  const x  = MARGIN_L_MM;
  const y  = MARGIN_T_MM;
  const w  = CONTENT_W_MM;
  const h  = CONT_HEADER_H_MM - 2; // box height; 2mm gap before canvas content
  const cy = y + h / 2 + 1.5;      // vertical centre for text baseline

  // Subtle cream background
  pdf.setFillColor(250, 249, 247);
  pdf.rect(x, y, w, h, "F");

  // Amber left accent bar
  pdf.setFillColor(212, 165, 116);
  pdf.rect(x, y, 3, h, "F");

  // Section title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(26, 31, 53);
  pdf.text(title, x + 7, cy);

  // "(continued)" right-aligned label
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(100, 116, 139);
  pdf.text("(continued)", x + w, cy, { align: "right" });

  // Amber bottom separator line
  pdf.setDrawColor(212, 165, 116);
  pdf.setLineWidth(0.4);
  pdf.line(x, y + h, x + w, y + h);
}

/* ---------------------------------------------------------
   Table of Contents page

   Drawn entirely with jsPDF vector/text APIs — no canvas.
   Called after all chapters are rendered so page numbers
   are exact. The page was reserved (pdf.addPage()) before
   any content chapters, and we fill it here with
   pdf.setPage(tocPageIndex).

   Layout:
     ┌────────────────────────────────────────────────┐ ← navy strip
     │  ✦  TABLE OF CONTENTS                          │
     │     Karmic Awareness Report · SysMindX Astro   │
     └────────────────────────────────────────────────┘
     Birth Chart ·················· 3
     Personality Traits ·········· 4
     …
     Glossary & Resources ········ 25
     ─────────────────────────────
     (footer note)
---------------------------------------------------------- */

interface TocEntry {
  title: string;
  page:  number;
}

function drawTOCPage(pdf: jsPDF, entries: TocEntry[]): void {
  const x = MARGIN_L_MM;
  const w = CONTENT_W_MM;
  let   y = MARGIN_T_MM;

  // ── Navy header strip ──────────────────────────────────────────
  const headerH = 38;

  pdf.setFillColor(26, 31, 53);                    // navy #1a1f35
  pdf.rect(x, y, w, headerH, "F");

  pdf.setFillColor(212, 165, 116);                 // amber accent bar
  pdf.rect(x, y, 4, headerH, "F");

  // Main title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(255, 255, 255);
  pdf.text("TABLE OF CONTENTS", x + 12, y + 15);

  // Sub-title
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(212, 165, 116);
  pdf.text(
    "Karmic Awareness Report  \u00b7  SysMindX Astro",
    x + 12,
    y + 27
  );

  y += headerH + 8;

  // ── Amber separator ────────────────────────────────────────────
  pdf.setDrawColor(212, 165, 116);
  pdf.setLineWidth(0.5);
  pdf.line(x, y, x + w, y);
  y += 10;

  // ── Chapter entry rows ─────────────────────────────────────────
  const rowH       = 13;
  const glossaryTitle = CHAPTER_TITLES["GLOSSARY"] ?? "Glossary & Resources";

  entries.forEach((entry, idx) => {
    const isGlossary = entry.title === glossaryTitle;

    // Separator before the Glossary row
    if (isGlossary) {
      pdf.setDrawColor(212, 165, 116);
      pdf.setLineWidth(0.25);
      pdf.line(x, y - 3, x + w, y - 3);
      y += 2;
    }

    // Alternating row tint
    if (idx % 2 === 0) {
      pdf.setFillColor(250, 249, 247);
      pdf.rect(x, y - 4, w, rowH, "F");
    }

    // Chapter title
    pdf.setFont("helvetica", isGlossary ? "italic" : "normal");
    pdf.setFontSize(11.5);
    pdf.setTextColor(26, 31, 53);
    pdf.text(entry.title, x + 8, y + 3);

    // Page number (amber)
    const pageStr = String(entry.page);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11.5);
    pdf.setTextColor(212, 165, 116);
    pdf.text(pageStr, x + w - 2, y + 3, { align: "right" });

    // Dotted leader between title and page number
    const titleEnd  = x + 8 + pdf.getTextWidth(entry.title) + 5;
    const pageStart = x + w - 2 - pdf.getTextWidth(pageStr) - 5;
    if (pageStart > titleEnd + 6) {
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.15);
      let dotX = titleEnd;
      while (dotX < pageStart - 2) {
        pdf.line(dotX, y + 2.5, dotX + 0.8, y + 2.5);
        dotX += 3.2;
      }
    }

    y += rowH;
  });

  // ── Footer note ────────────────────────────────────────────────
  y += 8;
  pdf.setDrawColor(220, 220, 220);
  pdf.setLineWidth(0.2);
  pdf.line(x, y, x + w, y);
  y += 7;

  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8.5);
  pdf.setTextColor(150, 150, 160);
  pdf.text(
    "Navigate to any section using the page numbers above. This report is for self-reflection only.",
    x + w / 2,
    y + 4,
    { align: "center" }
  );
}

/* ---------------------------------------------------------
   Core slicing — smart break-aware, with continuation headers

   Two slice-height values are used:
   • firstSliceHeightPx  — first page of a chapter (full content area)
   • contSliceHeightPx   — overflow pages (reduced by header height)
---------------------------------------------------------- */

function addCanvasToPdfWithClipping(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  isFirstChapter: boolean,
  breakCandidates: BreakCandidate[],
  chapterKey: string
) {
  const title = CHAPTER_TITLES[chapterKey] ?? "";

  const canvasW = canvas.width;
  const canvasH = canvas.height;

  // Canvas pixels per mm (width axis)
  const pxPerMm = canvasW / CONTENT_W_MM;

  // Slice heights for first vs continuation pages
  const firstSliceHeightPx = Math.round(CONTENT_H_MM                       * pxPerMm);
  const contSliceHeightPx  = Math.round((CONTENT_H_MM - CONT_HEADER_H_MM)  * pxPerMm);

  // Top margin for canvas content on continuation pages
  const contTopMM = MARGIN_T_MM + CONT_HEADER_H_MM;

  let currentY   = 0;
  let isFirstSlice = true;

  while (currentY < canvasH) {
    const isContinuation = !isFirstSlice;
    const sliceHeightPx  = isContinuation ? contSliceHeightPx : firstSliceHeightPx;
    const topMM          = isContinuation ? contTopMM         : MARGIN_T_MM;

    // Add a new PDF page for every slice except the very first of the
    // very first chapter (jsPDF already creates the initial page).
    if (!isFirstSlice || !isFirstChapter) {
      pdf.addPage();
    }

    // Draw the continuation header on overflow pages (not the first page
    // of a chapter — the chapter's own SectionHeader covers that).
    if (isContinuation && title) {
      drawContinuationHeader(pdf, title);
    }

    isFirstSlice = false;

    // Determine the actual cut Y using the smart finder
    const naturalEndY = currentY + sliceHeightPx;
    let endY: number;

    if (naturalEndY >= canvasH) {
      endY = canvasH; // last (or only) slice
    } else {
      endY = findSmartCutY(
        naturalEndY,
        currentY,
        breakCandidates,
        sliceHeightPx
      );
    }

    const srcH = endY - currentY;
    if (srcH <= 0) { currentY = endY + 1; continue; } // safety guard

    // Render this slice to a temporary canvas
    const sliceCanvas       = document.createElement("canvas");
    sliceCanvas.width  = canvasW;
    sliceCanvas.height = srcH;

    const ctx = sliceCanvas.getContext("2d")!;
    ctx.drawImage(
      canvas,
      0, currentY,    // source x, y
      canvasW, srcH,  // source w, h
      0, 0,           // dest x, y
      canvasW, srcH   // dest w, h
    );

    const imgData  = sliceCanvas.toDataURL("image/jpeg", 0.95);
    const destH_MM = srcH / pxPerMm;

    pdf.addImage(imgData, "JPEG", MARGIN_L_MM, topMM, CONTENT_W_MM, destH_MM);

    currentY = endY;
  }
}

/* ---------------------------------------------------------
   Footer
---------------------------------------------------------- */

function addFooterAndNumbering(pdf: jsPDF) {
  const totalPages = pdf.getNumberOfPages();

  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);

    const pageWidth  = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const y = pageHeight - 8;

    pdf.setDrawColor(220);
    pdf.setLineWidth(0.2);
    pdf.line(10, pageHeight - 12, pageWidth - 10, pageHeight - 12);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120);

    pdf.text("SysMindX Solutions", 10, y);
    pdf.text(
      "Self-reflection • Not prediction • Not professional advice",
      pageWidth / 2, y,
      { align: "center" }
    );
    pdf.text(`Page ${p} / ${totalPages}`, pageWidth - 10, y, {
      align: "right",
    });
  }
}

/* ---------------------------------------------------------
   Main Export Function
---------------------------------------------------------- */

export async function exportFullReport({
  result,
  effectivePlan,
  renderChapter,
  includeAiSummary = true,
}: {
  result: any;
  effectivePlan: string;
  renderChapter: (key: string) => Promise<void>;
  includeAiSummary?: boolean;
}) {
  if (!result) throw new Error("EXPORT_NO_RESULT");

  const pdf = new jsPDF("p", "mm", "a4");

  /* Helper: render a chapter into the hidden DOM container, capture
     it with html2canvas, and slice it onto the PDF.              */
  async function processChapter(key: string, isFirst: boolean) {
    await renderChapter(key);
    await new Promise((r) => setTimeout(r, 80));

    const el = document.getElementById("sx-export-chapter");
    if (!el) return;

    // Measure break candidates BEFORE html2canvas (positions match canvas pixels)
    const breakCandidates = getBreakCandidates(el, HTML2CANVAS_SCALE);

    const canvas = await html2canvas(el, {
      scale:       HTML2CANVAS_SCALE,
      backgroundColor: "#ffffff",
      useCORS:     true,
      scrollX:     0,
      scrollY:     0,
      windowWidth: 794,
    });

    addCanvasToPdfWithClipping(pdf, canvas, isFirst, breakCandidates, key);
  }

  // ── Step 1: COVER → fills page 1 ──────────────────────────────────
  await processChapter("COVER", true);

  // ── Step 2: Reserve the TOC page (page 2) — content filled later ──
  pdf.addPage();
  const tocPageIndex = pdf.getNumberOfPages(); // = 2

  // ── Step 2b: Pre-fetch AI summary before chapter loop ────────────
  //    Skipped entirely if user opted out (includeAiSummary = false).
  //    Attaches result.ai_summary so the AI_SUMMARY chapter can read it.
  //    Fails silently — AI_SUMMARY chapter shows a fallback if empty.
  if (includeAiSummary && CHAPTER_ORDER.includes("AI_SUMMARY")) {
    try {
      const apiBase =
        (process.env.REACT_APP_API_BASE_URL as string | undefined) ||
        "http://localhost:8000";

      // Build enriched domain detail (phase + identity + guidance per domain)
      const domainKeys = ["career", "wealth", "relationship", "vitality"];
      const domainsEnriched: Record<string, any> = {};
      for (const key of domainKeys) {
        const d = result?.phase_6?.[key];
        if (d) {
          domainsEnriched[key] = {
            phase:    d.phase_key     ?? "",
            identity: d.identity_text ?? "",
            guidance: d.guidance      ?? "",
          };
        }
      }

      // Build enriched dasha (planet + dates)
      const dashaEnriched: Record<string, any> = {};
      for (const tier of ["md", "ad", "pd"] as const) {
        const d = result?.dasha?.[tier];
        if (d?.planet) {
          dashaEnriched[tier] = {
            planet: d.planet ?? "",
            start:  d.start  ?? "",
            end:    d.end    ?? "",
          };
        }
      }

      // Build enriched remedies (primary + secondary, with role label)
      const roleMap: Record<string, string> = { md: "Mahadasha", ad: "Antardasha", pd: "Pratyantar" };
      const remediesEnriched: Record<string, any> = {};
      for (const tier of ["md", "ad", "pd"] as const) {
        const planet = result?.dasha?.[tier]?.planet;
        if (!planet) continue;
        const byPlanet = result?.remedies?.by_planet?.[planet] ?? {};
        remediesEnriched[planet] = {
          role:      roleMap[tier],
          primary:   (byPlanet.primary   ?? []).slice(0, 2).map((x: any) => x?.text ?? x),
          secondary: (byPlanet.secondary ?? []).slice(0, 2).map((x: any) => x?.text ?? x),
        };
      }

      const payload = {
        karmic_pattern: result?.karmic_overview_summary?.pattern      ?? "",
        description:    result?.karmic_overview_summary?.description   ?? "",
        domains:            domainsEnriched,
        dasha:              dashaEnriched,
        awareness_signals:  (result?.awareness_signals ?? [])
                              .slice(0, 3)
                              .map((s: any) => ({ signal_id: s.signal_id, theme: s.theme })),
        remedies:           remediesEnriched,
      };

      const res = await fetch(`${apiBase}/summarize`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        result.ai_summary = data.summary ?? "";
      } else {
        result.ai_summary = "";
      }
    } catch (_e) {
      result.ai_summary = "";
    }
  }

  // ── Step 3: Render all chapters after COVER, tracking start pages ──
  //    GLOSSARY is the last chapter in CHAPTER_ORDER.
  //    AI_SUMMARY is excluded if the user opted out.
  const contentChapters = CHAPTER_ORDER.filter(
    (k) => k !== "COVER" && (includeAiSummary || k !== "AI_SUMMARY")
  );
  const chapterStartPages: Record<string, number> = {};

  for (const key of contentChapters) {
    // Record page number of first page for this chapter BEFORE rendering it
    chapterStartPages[key] = pdf.getNumberOfPages() + 1;
    await processChapter(key, false);
  }

  // ── Step 4: Go back to the TOC page and draw it ───────────────────
  pdf.setPage(tocPageIndex);

  const tocEntries: TocEntry[] = contentChapters
    .filter((k) => CHAPTER_TITLES[k])        // skip chapters with no title (none currently)
    .map((k) => ({
      title: CHAPTER_TITLES[k],
      page:  chapterStartPages[k],
    }));

  drawTOCPage(pdf, tocEntries);

  // ── Step 5: Footer and page numbering across all pages ────────────
  addFooterAndNumbering(pdf);

  const filename = buildExportFilename(result);
  pdf.save(filename);
}
